import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  CheckCircleOutlined,
  SkipNextOutlined,
  ReplayOutlined,
  FlagOutlined,
} from '@mui/icons-material';
import type { FlatExecuteStep } from '@/types/run-stage.types';

interface BottomActionBarProps {
  currentStep: FlatExecuteStep | undefined;
  isSaving: boolean;
  isReadOnly: boolean;
  allStepsDone: boolean;
  hasCheckpointErrors: boolean;
  isLastStep?: boolean;
  onMarkComplete: () => void;
  onSkip: () => void;
  onRepeat: () => void;
  onFinish: () => void;
}

export const BottomActionBar: React.FC<BottomActionBarProps> = ({
  currentStep,
  isSaving,
  isReadOnly,
  allStepsDone,
  hasCheckpointErrors,
  isLastStep = false,
  onMarkComplete,
  onSkip,
  onRepeat,
  onFinish,
}) => {
  const [confirmDialog, setConfirmDialog] = useState<'skip' | 'repeat' | null>(null);

  if (isReadOnly) return null;

  const step = currentStep?.step;
  const isCompleted = step?.completed || step?.status === 'COMPLETED';
  const isSkipped = step?.status === 'SKIPPED';
  const isAborted = step?.status === 'ABORTED';
  const canComplete = step && !isCompleted && !isSkipped && !isAborted;

  const handleSkipConfirm = () => {
    setConfirmDialog(null);
    onSkip();
  };

  const handleRepeatConfirm = () => {
    setConfirmDialog(null);
    onRepeat();
  };

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
          {isSaving && <CircularProgress size={20} />}

          {/* Skip — show when step is not completed/skipped */}
          {canComplete && (
            <Button
              startIcon={<SkipNextOutlined />}
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => setConfirmDialog('skip')}
              disabled={isSaving}
            >
              Skip
            </Button>
          )}

          {/* Mark Completed / Finish — show for any non-completed step */}
          {canComplete && (
            <Tooltip
              title={hasCheckpointErrors ? 'Complete all required checkpoints first' : ''}
              placement="top"
            >
              <span>
                <Button
                  startIcon={isLastStep ? <FlagOutlined /> : <CheckCircleOutlined />}
                  variant="contained"
                  size="small"
                  color={isLastStep ? 'primary' : 'success'}
                  onClick={isLastStep ? onFinish : onMarkComplete}
                  disabled={isSaving || hasCheckpointErrors}
                >
                  {isLastStep ? 'Finish' : 'Mark Completed'}
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Repeat — show when COMPLETED */}
          {step && isCompleted && (
            <Button
              startIcon={<ReplayOutlined />}
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => setConfirmDialog('repeat')}
              disabled={isSaving}
            >
              Repeat
            </Button>
          )}

          {/* Finish (all done) — show when all steps completed but run not yet finished */}
          {allStepsDone && !isLastStep && (
            <Button
              startIcon={<FlagOutlined />}
              variant="contained"
              size="small"
              color="primary"
              onClick={onFinish}
              disabled={isSaving}
            >
              Finish Run
            </Button>
          )}
        </Stack>
      </Box>

      {/* Confirmation Dialogs */}
      <Dialog open={confirmDialog === 'skip'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Skip Step</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to skip this step? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} color="inherit">Cancel</Button>
          <Button onClick={handleSkipConfirm} color="warning" variant="contained">Skip Step</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'repeat'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Repeat Step</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Repeat this step? A new instance will be created after the current step.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} color="inherit">Cancel</Button>
          <Button onClick={handleRepeatConfirm} color="primary" variant="contained">Repeat Step</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
